// The two hours after you press Amend.
//
// Filing an amendment re-opens a locked month for writes for exactly two hours
// (20260829100000_period_lock.sql), and that window is not a formality — it is
// the ONLY time some of this work is possible:
//
//   • compensated_hours carries assert_period_open on both insert and approval
//     (20260829110000:194, :214; 20260902120000:116, :136), so crediting hours
//     into a closed month cannot happen outside it at all.
//   • time_corrections is deliberately NOT gated at filing time — a correction
//     against a locked day is legitimately pending, "waiting for an amendment"
//     (20260829100000:110-114). It is the APPROVAL that writes time_entries and
//     is refused until the window is open.
//
// Before this, pressing Amend filed the exception and dropped you back on the
// periods card with nothing to act on and a clock already running. Everything
// here is a surface that already existed somewhere else on the screen; what was
// missing was having them in the window together.
//
// On the Dialog primitive (HRA-V1). It was a hand-built overlay that closed on a
// window-level Escape, so it carried a `granting` guard to stop an Escape meant
// for the Credit hours dialog on top from shutting the whole workspace. A modal
// <dialog> owns its keys: the one on top answers Escape and this one never sees
// it. Below 480 that makes it a bottom sheet with no phone variant here: the
// clock and the steps scroll in its body, and Close stays at its foot
// (UI-CONVENTIONS §8).
//
// Deliberately NOT here: any write path to payroll_adjustment. It has no insert
// policy by design (20260829120000_payroll.sql:210-212) and
// settle_period_amendment is its only writer — which is why this points at
// Settle variance on the register rather than growing a button of its own.
import { useEffect, useRef, useState } from 'react';
import { Button, Icon, Mono } from './primitives';
import { Dialog } from './dialog';
import { InlineStatus } from './feedback';
import { MonthlySummary } from './MonthlySummary';
import { CompHoursModal } from './compHours';
import { listCategories, type Category } from '../lib/compHours';
import { CorrectionDecisions } from './requests';
import { listCorrections, type PendingCorrection } from '../lib/timeCorrections';
import { orgToday } from '../lib/dates';
import { clockTime, monthLabel } from '../lib/payrollPeriods';
import type { OrgHours } from '../lib/orgHours';
import type { Workdays } from '../lib/workdays';

/**
 * Months back from the current org month — what MonthlySummary's stepper counts
 * in. Built from month indices rather than Date arithmetic, because setMonth()
 * overflows on the 31st.
 */
export const offsetToMonth = (year: number, month: number, today: Date): number =>
  year * 12 + (month - 1) - (today.getFullYear() * 12 + today.getMonth());

/** Last day of the month, as an ISO key. Day 0 of the NEXT month, which is how
 *  you get a month's length without a table of them. */
export const monthEnd = (year: number, month: number): string =>
  `${year}-${String(month).padStart(2, '0')}-${String(new Date(year, month, 0).getDate()).padStart(2, '0')}`;

export function AmendWorkspace({
  year,
  month,
  tz,
  org,
  workdays,
  closesAt,
  uid,
  onClose,
}: {
  year: number;
  month: number;
  tz: string;
  org: OrgHours;
  workdays: Workdays | null;
  /** When the window shuts, or null if it already has. */
  closesAt: Date | null;
  /** The approver, for deciding corrections. The trigger overwrites it with
   *  auth.uid() and refuses outright if the decider is the subject. */
  uid: string;
  onClose: () => void;
}) {
  const [cats, setCats] = useState<Category[]>([]);
  const [granting, setGranting] = useState<{ id: string; name: string | null } | null>(null);
  // The receipt for a credit. A page Toast is held under this open dialog, so
  // the answer is said here (UI-CONVENTIONS: feedback in a dialog stays in it).
  const [credited, setCredited] = useState<string | null>(null);
  // Focus lands on the receipt: the Credit hours button that opened the form is
  // gone (the summary remounts with the new figure), and every control behind
  // this dialog is inert, so the closing form's return-focus fell to <body>.
  const creditedRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (credited) creditedRef.current?.focus();
  }, [credited]);
  // undefined = still loading · null = the read failed · [] = none waiting.
  // Starting at [] printed "None waiting" before the read had even returned.
  const [corrections, setCorrections] = useState<PendingCorrection[] | null | undefined>(undefined);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let live = true;
    void listCategories().then((c) => live && setCats(c));
    return () => {
      live = false;
    };
  }, []);

  // Pending corrections whose work_date falls inside the month being amended.
  // Approving one writes time_entries, which assert_period_open refuses unless
  // this window is open — so this list is the single most likely REASON to have
  // opened it, and it used to be two screens away.
  useEffect(() => {
    let live = true;
    void listCorrections().then((rows) => live && setCorrections(rows));
    return () => {
      live = false;
    };
  }, [nonce]);

  // [9] Re-render when the window shuts. `open` is read off the clock at render,
  // and nothing else re-renders an idle modal — so without this, one left open
  // past its deadline kept offering buttons the database refuses. Reuses `nonce`,
  // which also refetches corrections; harmless.
  useEffect(() => {
    if (!closesAt) return;
    const ms = closesAt.getTime() - Date.now();
    if (ms <= 0) return;
    const t = setTimeout(() => setNonce((n) => n + 1), ms + 1000);
    return () => clearTimeout(t);
  }, [closesAt]);

  const label = monthLabel(year, month);
  // The clock, not just presence. 'Open workspace' reads its closesAt from a
  // card that is not re-rendered while idle, so it can hand in one that has
  // already passed — and every write would then be refused.
  const open = closesAt != null && closesAt.getTime() > Date.now();
  const from = `${year}-${String(month).padStart(2, '0')}-01`;
  const to = monthEnd(year, month);
  const inMonth = (corrections ?? []).filter(
    (c) => c.status === 'pending' && c.work_date >= from && c.work_date <= to,
  );
  const note = { fontSize: 'var(--hf-text-sm)', color: 'var(--hf-ink-3)' } as const;
  const strong = { color: 'var(--hf-ink-2)', fontWeight: 'var(--hf-weight-medium)' } as const;

  return (
    <Dialog
      open
      title={`Amending ${label}`}
      size="lg"
      onClose={onClose}
      footer={
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--hf-space-4)' }}>
        {/* The clock is the whole reason this screen exists, so it is the first
            thing on it. Static rather than a countdown: a stale minute costs one
            legible refusal from the database, and a ticking interval costs a
            re-render a second behind a modal nobody is watching the seconds on. */}
        {open ? (
          <InlineStatus tone="warning">
            Writes to {label} are open until <Mono>{clockTime(closesAt)}</Mono>. Time entries,
            attendance, leave and credited hours can be changed until then.
          </InlineStatus>
        ) : (
          <InlineStatus tone="error">
            The amendment window for {label} has closed. Nothing here can be changed until somebody
            files a new amendment.
          </InlineStatus>
        )}

        <ol
          style={{
            ...note,
            margin: 0,
            paddingLeft: 'var(--hf-space-5)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--hf-space-1)',
            lineHeight: 'var(--hf-leading-body)',
          }}
        >
          <li>
            Approve or decline any time corrections below — that is the write the window exists for.
          </li>
          <li>
            Use <strong style={strong}>Credit hours</strong> on a row to add compensated hours to
            somebody&rsquo;s month.
          </li>
          <li>
            Leave for this month is approved or reverted on{' '}
            <strong style={strong}>Requests &amp; approvals</strong>; it is gated by this same
            window, so do it before the clock runs out.
          </li>
          <li>
            Then press <strong style={strong}>Settle variance</strong> on the register to turn the
            difference into an adjustment on the next run.
          </li>
        </ol>

        <section aria-labelledby="amend-corrections">
          <h3
            id="amend-corrections"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--hf-space-2)',
              margin: '0 0 var(--hf-space-2)',
              fontSize: 'var(--hf-text-sm)',
              fontWeight: 'var(--hf-weight-medium)',
              color: 'var(--hf-ink-2)',
            }}
          >
            {/* The flag is a correction everywhere (UI-CONVENTIONS, Icons). */}
            <Icon name="flag" size={13} />
            Time corrections dated in {label}
          </h3>
          {corrections === undefined ? (
            <div style={note}>Loading time corrections…</div>
          ) : corrections === null ? (
            <InlineStatus
              tone="error"
              action={{ label: 'Retry', onClick: () => setNonce((n) => n + 1) }}
            >
              Time corrections could not be loaded, so any waiting for this month are not shown.
            </InlineStatus>
          ) : !open && inMonth.length > 0 ? (
            <div style={note}>
              {inMonth.length} waiting, but the window has closed — approving one writes a time
              entry, which the period lock refuses.
            </div>
          ) : (
            // Mounted with no rows too, so deciding the last one keeps its receipt.
            <>
              <CorrectionDecisions
                rows={inMonth}
                uid={uid}
                onChanged={() => setNonce((n) => n + 1)}
              />
              {inMonth.length === 0 && <div style={note}>None waiting for {label}.</div>}
            </>
          )}
        </section>

        {/* Pinned to the month being amended. Org-wide on purpose: an amendment
            is about a payroll period, and you do not know in advance whose row
            is wrong.

            `pinned` hides the stepper. Stepping to July while amending August and
            pressing Credit hours on a July row pre-filled 31 August — the hours
            landed in a month other than the one on screen.

            `key={nonce}` remounts it after every write. MonthlySummary refetches
            only when its range changes, so crediting 4h left the row showing the
            old figure with no confirmation anywhere — it looked unsaved, inviting
            a second credit that Settle variance would then carry into payroll. */}
        {credited && (
          <div ref={creditedRef} tabIndex={-1}>
            <InlineStatus tone="success">{credited}</InlineStatus>
          </div>
        )}
        <MonthlySummary
          key={nonce}
          pinned
          tz={tz}
          org={org}
          workdays={workdays}
          initialOffset={offsetToMonth(year, month, orgToday(tz))}
          exportable
          rowAction={
            open
              ? // `uid` IS the viewer's id here (it's what CorrectionDecisions
                // above already use as the approver) — no need to ask useAuth() for
                // what this component was already handed. The trigger refuses a
                // self-grant outright, so the row simply offers nothing instead of a
                // button that would only round-trip into an error.
                (r) =>
                  r.profileId === uid ? null : (
                    <Button
                      variant="secondary"
                      size="sm"
                      icon="award"
                      // The workspace's task on a phone: one per row, so the
                      // 44px areas a finger gets stay inside their rows.
                      className="hf-hit"
                      onClick={() => {
                        setCredited(null);
                        setGranting({ id: r.profileId, name: r.name });
                      }}
                    >
                      Credit hours
                    </Button>
                  )
              : undefined
          }
        />

        {/* A modal on top of this one, rendered as a child of this <dialog> and
            never portalled to <body>: showModal() makes everything outside the
            top dialog inert, so a portalled form would sit under this one,
            unreachable. A nested <dialog> opens above it, owns its Escape, and
            leaves this one open. */}
        {granting && (
          <CompHoursModal
            mode="grant"
            uid={granting.id}
            personName={granting.name}
            categories={cats}
            // `today` is both the default date AND the input's max. Passing the
            // real today would open the picker on a day OUTSIDE the month being
            // amended, which assert_period_open refuses — you would fill the form
            // in and be rejected. The last day of the amended month is inside the
            // window by construction, and it cannot be in the future: a month
            // cannot be locked, and so cannot be amended, until it has ended.
            today={monthEnd(year, month)}
            onClose={() => setGranting(null)}
            onFiled={() => {
              setCredited(`Hours credited${granting.name ? ` to ${granting.name}` : ''}.`);
              setGranting(null);
              setNonce((n) => n + 1);
            }}
          />
        )}
      </div>
    </Dialog>
  );
}
